import React, { useEffect, useMemo, useState } from 'react';
import { Alert, PageHeader } from '@/components/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import { taskService } from '../../../services/taskService';
import { formService } from '../../../services/formService';
import { usePermissions } from '../../../hooks/usePermissions';
import { HE_AUTO_APPROVE_PERMISSION } from '../../forms/utils/submissionPermissions';
import HeVendorApprovalEmailModal from '../components/HeVendorApprovalEmailModal';
import type { TaskType } from '../../../types/Task';
import HiredEquipmentGrid from '../components/HiredEquipmentGrid';
import HiredEquipmentInternalComment from '../components/HiredEquipmentInternalComment';
import HiredEquipmentTaskModals, {
  type HiredEquipmentTaskModalRequest,
} from '../components/HiredEquipmentTaskModals';
import PoBalanceWarningModal from '../components/PoBalanceWarningModal';
import SubmissionConfirmationModal from '../components/SubmissionConfirmationModal';
import { useHiredEquipmentGridData } from '../hooks/useHiredEquipmentGridData';
import { usePoBalanceWarning } from '../hooks/usePoBalanceWarning';
import type { HiredEquipmentAnchor, HiredEquipmentGridRow, TaskOption } from '../types';
import { persistHiredEquipmentAnchor, readHiredEquipmentAnchor, clearHiredEquipmentAnchor } from '../utils/anchorStorage';
import { formatAnchorValue, hiredEquipmentGridPageTitle, methodLabel } from '../utils/buildAnchor';
import { defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { sumAmounts } from '../utils/gridMath';
import { heGridSubmitRows } from '../utils/heSummaryRows';
import {
  formatIncompleteLinesMessage,
  scrollToGridLine,
  validateGridRows,
} from '../utils/gridValidation';
import {
  appendMissingPoResources,
  applyClonedTaskToRow,
  duplicateRow,
  emptyRowFromAnchor,
  formatTaskLabel,
  isRoutineSpecificTask,
  lumpSumRowFromAnchor,
  rowsFromPoResources,
  toTaskOption,
} from '../utils/poPrefill';
import { nextRowAfterTaskChange } from '../utils/poTaskFilter';
import { submitHiredEquipment } from '../../../services/hiredEquipmentService';
import { persistSessionSubmission } from '../utils/sessionSubmissions';

interface GridLocationState {
  anchor?: HiredEquipmentAnchor;
}

const HiredEquipmentGridPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateAnchor = (location.state as GridLocationState | null)?.anchor;
  const [anchor, setAnchor] = useState<HiredEquipmentAnchor | null>(() => stateAnchor ?? readHiredEquipmentAnchor());
  const [rows, setRows] = useState<HiredEquipmentGridRow[]>([]);
  const [prePopulateAttempted, setPrePopulateAttempted] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    referenceNumber: string;
    formId: string;
    attachmentCount: number;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [internalComment, setInternalComment] = useState('');
  const [taskModalRequest, setTaskModalRequest] = useState<HiredEquipmentTaskModalRequest | null>(null);
  /** Rows whose task clone is still in flight — their Duplicate and task picker are disabled. */
  const [cloningClientIds, setCloningClientIds] = useState<Set<string>>(new Set());
  const [cloneError, setCloneError] = useState<string | null>(null);
  /** Submitted and awaiting the vendor-email answer that its approval travels with. */
  const [pendingAutoApproval, setPendingAutoApproval] = useState<{
    formId: string;
    referenceNumber: string;
  } | null>(null);
  const [approving, setApproving] = useState(false);

  const { hasPermission } = usePermissions();
  const lookups = useHiredEquipmentGridData(anchor ?? { entryMethod: 'purchase_order' }, rows);
  const poBalanceWarning = usePoBalanceWarning();

  const availableTaskTypes = useMemo<TaskType[]>(() => {
    const types: TaskType[] = [];
    if (hasPermission('routine:specific_create')) types.push('Routine');
    if (hasPermission('quantified:task_create')) types.push('Quantified');
    return types;
  }, [hasPermission]);

  /** This user's own submissions go in approved rather than pending. */
  const canAutoApprove = hasPermission(HE_AUTO_APPROVE_PERMISSION);

  useEffect(() => {
    if (stateAnchor) {
      persistHiredEquipmentAnchor(stateAnchor);
      setAnchor(stateAnchor);
    }
  }, [stateAnchor]);

  useEffect(() => {
    if (!anchor && !confirmation) {
      navigate('/forms', { replace: true });
    }
  }, [anchor, confirmation, navigate]);

  useEffect(() => {
    if (!anchor || prePopulateAttempted) return;
    setPrePopulateAttempted(true);

    const seed = async () => {
      if (anchor.prePopulate && anchor.lockedPoId) {
        try {
          const resources = await hiredEquipmentPoService.getPoResources(anchor.lockedPoId);
          if (resources.length > 0) {
            setRows(rowsFromPoResources(anchor, resources));
            return;
          }
        } catch (error) {
          console.error('Failed to pre-populate hired equipment grid from PO', error);
        }
      }
      setRows([]);
    };

    seed();
  }, [anchor, prePopulateAttempted]);

  useEffect(() => {
    if (lookups.vendors.length === 0) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.fuelSurchargeEligible != null) return row;
        if (row.vehicleType) {
          const fallback = defaultFuelSurchargeEligibleFromVehicleType(row.vehicleType);
          if (fallback == null) return row;
          changed = true;
          return { ...row, fuelSurchargeEligible: fallback };
        }
        if (row.vendorId == null || row.resourceId == null) return row;
        const resource = lookups.resourcesForRow(row).find((item) => item.id === row.resourceId);
        if (!resource?.vehicleType) return row;
        changed = true;
        return {
          ...row,
          vehicleType: resource.vehicleType,
          fuelSurchargeEligible: defaultFuelSurchargeEligibleFromVehicleType(resource.vehicleType),
        };
      });
      return changed ? next : prev;
    });
  }, [lookups.vendors, lookups.resourcesForRow]);

  if (!anchor) return null;

  const handleAddLine = () => {
    setRows((prev) => [...prev, emptyRowFromAnchor(anchor)]);
  };

  const handleAddLumpSumLine = () => {
    setRows((prev) => [...prev, lumpSumRowFromAnchor(anchor)]);
  };

  /**
   * Duplicate a line, and give the copy its own task when the original carries a specific routine
   * task. Two lines pointing at one task bill the same task twice; a routine task here represents
   * one piece of work, so the copy needs its own.
   *
   * The row is appended immediately and the clone is applied when it lands — duplicating stays
   * instant, and a failed clone leaves a usable line on the original task rather than nothing.
   */
  const handleDuplicate = async (clientId: string) => {
    const source = rows.find((row) => row.clientId === clientId);
    if (!source) return;

    const copy = duplicateRow(source);
    setRows((prev) => [...prev, copy]);

    // Cloning writes a real task, so it needs the same permission that gates creating one here.
    if (source.taskId == null || availableTaskTypes.length === 0) return;

    setCloningClientIds((prev) => new Set(prev).add(copy.clientId));
    try {
      const classified = await lookups.ensureTaskClassification(source.taskId);
      if (!classified || !isRoutineSpecificTask(classified)) return;

      // Pass the name through so the clone does not come back as "… (clone)" — this label is read
      // by the vendor on the submission PDF.
      const cloned = await taskService.cloneTask(source.taskId, {
        name: classified.name ?? classified.description,
        status: 'ACTIVE',
      });
      const option = toTaskOption(cloned);
      lookups.registerTask(option);
      setRows((prev) =>
        prev.map((row) => (row.clientId === copy.clientId ? applyClonedTaskToRow(row, option) : row)),
      );
    } catch (error) {
      console.error('Failed to clone the task for a duplicated hired equipment line', error);
      setCloneError(
        `Couldn't create a copy of task ${source.taskLabel || source.taskId}. The duplicated line still uses the original task.`,
      );
    } finally {
      setCloningClientIds((prev) => {
        const next = new Set(prev);
        next.delete(copy.clientId);
        return next;
      });
    }
  };

  const handleRemove = (clientId: string) => {
    setRows((prev) => prev.filter((row) => row.clientId !== clientId));
  };

  const handleReAddPoResources = async () => {
    if (!anchor.lockedPoId) return;
    const resources = await hiredEquipmentPoService.getPoResources(anchor.lockedPoId);
    setRows((prev) => appendMissingPoResources(anchor, prev, resources));
  };

  const handleDiscard = () => {
    clearHiredEquipmentAnchor();
    navigate('/forms');
  };

  const handleTaskCreated = (rowClientId: string, task: TaskOption) => {
    lookups.registerTask(task);
    setRows((prev) =>
      prev.map((row) =>
        row.clientId === rowClientId
          ? {
              ...row,
              // keepPo: a task created just now cannot be on any PO's line items, which are server
              // data that predate it, so revalidating the PO against it would always release it.
              ...nextRowAfterTaskChange(row, task, lookups.tasksByPoId, formatTaskLabel, true),
              // The submission owns a task it created, so approval may close it.
              taskCreatedInSession: true,
              // This path bypasses the grid's own `patch`, which is what normally clears the
              // duplicate shading — picking a task is an edit, so clear it here.
              duplicatedPending: false,
            }
          : row,
      ),
    );
  };

  const handleTaskUpdated = (task: TaskOption) => {
    lookups.registerTask(task);
    const label = formatTaskLabel(task);
    setRows((prev) =>
      prev.map((row) => (row.taskId === task.id && row.taskLabel !== label ? { ...row, taskLabel: label } : row)),
    );
  };

  const validation = validateGridRows(rows);
  const showValidation = attemptedSubmit && !validation.ok;

  /** Record the submission for this session and show the confirmation screen. */
  const finishSubmission = (
    submitted: { formId: string; referenceNumber: string },
    status: 'pending' | 'approved',
  ) => {
    persistSessionSubmission({
      id: submitted.referenceNumber,
      formId: submitted.formId,
      createdAt: new Date().toISOString(),
      anchor,
      rows,
      lineCount: rows.length,
      totalValue: sumAmounts(rows),
      status,
    });
    clearHiredEquipmentAnchor();
    setConfirmation({
      referenceNumber: submitted.referenceNumber,
      formId: submitted.formId,
      attachmentCount: rows.filter((row) => row.attachment).length,
    });
  };

  const persistAndSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const submitted = await submitHiredEquipment({
        anchor: {
          entryMethod: anchor.entryMethod,
          lockedVendorName: anchor.lockedVendorName,
          lockedPoNumber: anchor.lockedPoNumber,
          serviceAreaId: anchor.serviceAreaId,
        },
        rows: heGridSubmitRows(rows),
        internalComment,
      });

      // The submission always lands as pending first: approving it is a second, separate call, and
      // the vendor-email prompt needs a real form id to build its preview from. Asking now means
      // the prompt is answered before anything is approved, and a user who backs out is left with
      // an ordinary pending submission rather than a half-approved one.
      if (canAutoApprove) {
        setPendingAutoApproval(submitted);
        return;
      }

      finishSubmission(submitted, 'pending');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit hired equipment');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Second phase of an auto-approved submission: the user has answered the vendor-email prompt, so
   * approve it and let the answer travel with the approval, exactly as the approvals screen does.
   */
  const completeAutoApproval = async (
    emailVendorPdf: boolean,
    vendorEmail?: { to: string; subject: string; bodyText: string },
  ) => {
    if (!pendingAutoApproval || approving) return;
    setApproving(true);
    try {
      await formService.approveForm(pendingAutoApproval.formId, undefined, {
        emailVendorPdf,
        vendorEmail,
      });
      finishSubmission(pendingAutoApproval, 'approved');
    } catch (error) {
      console.error('Failed to auto-approve the hired equipment submission', error);
      // The submission itself is safely recorded, so say what happened and leave it pending for a
      // normal approver rather than losing the whole entry.
      setSubmitError(
        `Submitted as ${pendingAutoApproval.referenceNumber}, but approving it automatically failed. It is waiting for approval.`,
      );
      finishSubmission(pendingAutoApproval, 'pending');
    } finally {
      setApproving(false);
      setPendingAutoApproval(null);
    }
  };

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    setSubmitError(null);
    const result = validateGridRows(rows);
    if (!result.ok) {
      const firstLine = result.invalidLineNumbers[0];
      if (firstLine != null) scrollToGridLine(firstLine);
      return;
    }
    const okToSubmit = await poBalanceWarning.check({
      rows,
      lockedPoId: anchor.lockedPoId,
      purchaseOrders: lookups.purchaseOrders,
      fuelSurcharges: lookups.fuelSurcharges,
    });
    if (!okToSubmit) return;
    await persistAndSubmit();
  };

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-p-4">
      <PageHeader
        title={hiredEquipmentGridPageTitle(anchor)}
        subtitle={`Entry method: ${methodLabel(anchor.entryMethod)}`}
        actions={[
          {
            key: 'discard',
            label: 'Discard',
            icon: 'bi-x-lg',
            intent: 'neutral',
            variant: 'light',
            alwaysShowLabel: true,
            onClick: handleDiscard,
          },
          {
            key: 'submit',
            label: submitting ? 'Submitting…' : poBalanceWarning.checking ? 'Checking…' : 'Submit for approval',
            icon: 'bi-check-lg',
            intent: 'primary',
            alwaysShowLabel: true,
            disabled: submitting || poBalanceWarning.checking,
            onClick: handleSubmit,
          },
        ]}
      />

      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-px-4 tw-py-3 tw-shadow-sm">
        <div className="tw-min-w-0">
          <p className="tw-mb-0.5 tw-font-display tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
            Anchor
          </p>
          <p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-content">{formatAnchorValue(anchor)}</p>
        </div>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          <span className="tw-mr-1 tw-inline-block tw-h-2 tw-w-2 tw-rounded-full tw-bg-success" aria-hidden="true" />
          Mandatory fields marked * — enforced before submit
        </p>
      </div>

      {showValidation && (
        <Alert variant="danger" className="tw-mb-3">
          {rows.length === 0 ? (
            'Add at least one line before submitting.'
          ) : (
            <>
              <span className="tw-font-semibold">{formatIncompleteLinesMessage(validation.invalidLineNumbers)}</span>
              {' Jump to '}
              {validation.invalidLineNumbers.map((lineNumber, index) => (
                <React.Fragment key={lineNumber}>
                  {index > 0 && (index === validation.invalidLineNumbers.length - 1 ? ' and ' : ', ')}
                  <button
                    type="button"
                    className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-inherit tw-underline"
                    onClick={() => scrollToGridLine(lineNumber)}
                  >
                    line {lineNumber}
                  </button>
                </React.Fragment>
              ))}
              {'. Date, Task, Vendor, Resource, Qty, and Rate are required. Lump sum rows need Date, Task, Vendor, and an Amount over $0.'}
            </>
          )}
        </Alert>
      )}

      {submitError && (
        <Alert variant="danger" className="tw-mb-3">
          {submitError}
        </Alert>
      )}

      {cloneError && (
        <Alert variant="warning" className="tw-mb-3" onClose={() => setCloneError(null)} dismissible>
          {cloneError}
        </Alert>
      )}

      <HiredEquipmentGrid
        anchor={anchor}
        rows={rows}
        onRowsChange={setRows}
        vendors={lookups.vendors}
        taskOptionForRow={lookups.taskOptionForRow}
        searchTasks={lookups.searchTasks}
        purchaseOrders={lookups.purchaseOrders}
        tasksByPoId={lookups.tasksByPoId}
        resourcesForRow={lookups.resourcesForRow}
        fuelSurcharges={lookups.fuelSurcharges}
        attemptedSubmit={attemptedSubmit}
        onAddLine={handleAddLine}
        onAddLumpSumLine={handleAddLumpSumLine}
        onDuplicateLine={handleDuplicate}
        onRemoveLine={handleRemove}
        onReAddPoResources={anchor.entryMethod === 'purchase_order' ? handleReAddPoResources : undefined}
        onCreateTask={
          availableTaskTypes.length > 0
            ? (row) => setTaskModalRequest({ kind: 'create', rowClientId: row.clientId })
            : undefined
        }
        onViewTask={(task) => setTaskModalRequest({ kind: 'view', taskId: task.id })}
        cloningClientIds={cloningClientIds}
      />

      <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content-muted">
        Amount = qty × rate, plus fuel $/hr on regular hours when eligible. Overtime hours (after 8) bill at
        1.5× / 2× the rate without fuel. Markup is applied later from the task costing, not on this form.
        Fuel is looked up from the fuels table by On-Road / Off-Road / N/A and the line date. A lump sum
        row bills the amount you type, once — no resource, qty, rate, fuel, or overtime.
      </p>

      <HiredEquipmentInternalComment
        value={internalComment}
        onChange={setInternalComment}
        disabled={submitting}
        className="tw-mt-4"
      />

      <HiredEquipmentTaskModals
        request={taskModalRequest}
        onClose={() => setTaskModalRequest(null)}
        onTaskCreated={handleTaskCreated}
        onTaskUpdated={handleTaskUpdated}
        availableTypes={availableTaskTypes}
      />

      <PoBalanceWarningModal
        show={poBalanceWarning.warnings != null}
        warnings={poBalanceWarning.warnings ?? []}
        onCancel={poBalanceWarning.dismiss}
        onConfirm={() => {
          poBalanceWarning.dismiss();
          void persistAndSubmit();
        }}
      />

      {/*
        Auto-approve only. Shown after the submission exists but before it is approved, so the
        answer can travel with the approval call — the same contract the approvals screen uses.
        Cancelling leaves an ordinary pending submission.
      */}
      <HeVendorApprovalEmailModal
        isOpen={pendingAutoApproval != null}
        formId={pendingAutoApproval?.formId ?? null}
        busy={approving}
        onCancel={() => {
          if (approving || !pendingAutoApproval) return;
          const submitted = pendingAutoApproval;
          setPendingAutoApproval(null);
          finishSubmission(submitted, 'pending');
        }}
        onSkip={() => completeAutoApproval(false)}
        onSend={(draft) => completeAutoApproval(true, draft)}
      />

      <SubmissionConfirmationModal
        show={confirmation != null}
        referenceNumber={confirmation?.referenceNumber ?? ''}
        attachmentCount={confirmation?.attachmentCount ?? 0}
        onClose={() => navigate('/forms', { replace: true })}
        onViewApprovals={() =>
          navigate(`/approvals?formId=${encodeURIComponent(confirmation?.formId ?? '')}`)
        }
      />
    </div>
  );
};

export default HiredEquipmentGridPage;
